/**
 * Adapters for championship match replay (CIA, etc.) → Entraînement / GameHistory.
 */

import type { GameHistory } from '../game/gameHistory';
import type { CardNumber } from '../types/game';
import { sortHand } from './cards';
import { buildGameHistoryFromPartie, type ParsedPartie } from './differeReplay';

export interface ReplayTableJoueur {
  position: number;
  pseudo: string;
  pseudo_display?: string;
  is_preneur?: boolean;
}

export interface ReplayTable {
  id: number;
  numtable: number;
  numdonne: number;
  joueurs: ReplayTableJoueur[];
  preneur: number;
  contrat: number;
  contrat_label?: string;
  chien: number[];
  mains: Record<string, number[]>;
  plis: number[][];
  entames: number[];
  scoreduplicate?: number;
}

export interface ReplayDonneListItem {
  numdonne: number;
  score_table1: number | null;
  score_table2: number | null;
}

export function replayTableToPartie(table: ReplayTable): ParsedPartie {
  return {
    id: table.id,
    idtournoi: 0,
    numdonne: table.numdonne,
    playerCount: 4,
    joueurs: table.joueurs.map((j) => ({
      position: j.position,
      pseudo: j.pseudo_display || j.pseudo,
      is_preneur: Boolean(j.is_preneur),
    })),
    preneur: Number(table.preneur),
    contrat: Number(table.contrat) || 1,
    contrat_label: table.contrat_label || '',
    chien: table.chien,
    mains: table.mains,
    plis: table.plis,
    entames: table.entames,
    scoreduplicate: table.scoreduplicate,
  };
}

export function findPlayerPosition(joueurs: ReplayTableJoueur[], pseudo: string): number {
  const me = pseudo.toLowerCase();
  const found = joueurs.find((j) => j.pseudo.toLowerCase() === me);
  return found?.position ?? 0;
}

export function buildGameHistoryFromReplayTable(
  table: ReplayTable,
  viewerPseudo?: string,
): GameHistory {
  const history = buildGameHistoryFromPartie(replayTableToPartie(table));
  if (viewerPseudo) {
    history.myPosition = findPlayerPosition(table.joueurs, viewerPseudo);
  }
  return history;
}

export function buildReplaySeatLabels(table: ReplayTable): string[] {
  const labels: string[] = [];
  for (let pos = 0; pos < 4; pos++) {
    const j = table.joueurs.find((p) => p.position === pos) ?? table.joueurs[pos];
    labels.push(j?.pseudo_display || j?.pseudo || `J${pos + 1}`);
  }
  return labels;
}

export function gameHistoryToTrainingGameData(history: GameHistory) {
  return {
    players: history.players.map((p) => ({
      position: p.position,
      displayName: p.name,
      isHuman: !p.name.startsWith('Bot_'),
      isBot: p.name.startsWith('Bot_'),
    })),
    playerCount: history.playerCount,
    dealer: history.dealer,
    taker: history.taker,
    contract: history.contract,
    initialHands: Object.fromEntries(history.initialHands),
    chien: [...history.chien],
    ecart: [...history.ecart],
    bids: history.bids.map((b) => ({ player: b.player, bid: b.bid, timestamp: b.timestamp })),
    tricks: history.tricks.map((t) => ({
      trickNumber: t.trickNumber,
      leader: t.leader,
      cards: t.cards.map((c) => ({ player: c.player, card: c.card, timestamp: c.timestamp })),
      winner: t.winner,
    })),
    poignees: {},
  };
}

export type TrainingGameData = ReturnType<typeof gameHistoryToTrainingGameData>;

/**
 * Inverse de gameHistoryToTrainingGameData : reconstitue un GameHistory à
 * partir des données d'une variante, sans repasser par le moteur de jeu. Sert à
 * afficher la table de référence en entraînement.
 */
export function buildGameHistoryFromTrainingGameData(
  gameData: TrainingGameData,
  seatLabels?: string[],
  myPosition = 0,
): GameHistory {
  const playerCount = Number(gameData.playerCount) || 4;
  const contract = Number(gameData.contract) as GameHistory['contract'];

  const players = Array.from({ length: playerCount }, (_, pos) => {
    const src = gameData.players.find((p) => Number(p.position) === pos);
    return {
      position: pos,
      name: seatLabels?.[pos] || src?.displayName || `J${pos + 1}`,
      isConnected: true,
    };
  });

  const initialHands = new Map<number, CardNumber[]>();
  for (const [pos, hand] of Object.entries(gameData.initialHands ?? {})) {
    initialHands.set(Number(pos), sortHand([...(hand as CardNumber[])]));
  }

  const chien = [...(gameData.chien ?? [])] as CardNumber[];
  const ecart = [...(gameData.ecart ?? [])] as CardNumber[];
  const bids = (gameData.bids ?? []).map((b) => ({ ...b }));
  const tricks = (gameData.tricks ?? []).map((t) => ({
    trickNumber: t.trickNumber,
    leader: t.leader,
    cards: t.cards.map((c) => ({ ...c })),
    winner: t.winner,
  }));

  // Même ordre canonique que buildGameHistoryFromPartie : le prélude est
  // variable selon le contrat, d'où la synchronisation par curseur logique.
  const steps: GameHistory['steps'] = [{ type: 'distribution', label: 'DISTRIB' }];
  if (bids.length > 0) steps.push({ type: 'distribution', label: 'ENCH' });
  if (chien.length > 0 && contract <= 2) steps.push({ type: 'chien', label: 'CHIEN' });
  if (ecart.length > 0 && contract <= 2) steps.push({ type: 'ecart', label: 'ECART' });
  for (const trick of tricks) {
    steps.push({ type: 'trick', trickNumber: trick.trickNumber, label: `${trick.trickNumber}` });
  }

  return {
    players,
    playerCount,
    dealer: Number(gameData.dealer) || 0,
    taker: Number(gameData.taker) || 0,
    contract,
    myPosition,
    initialHands,
    chien,
    ecart,
    bids,
    tricks,
    poignees: [],
    score: undefined,
    steps,
  };
}

/** Équipe affichée sur une table (match par équipes). */
export interface CompareReplayTeamSide {
  id: number;
  nom: string;
}

/** Variante duplicate (table 1 ou 2) pour une même donne CIA. */
export interface CompareReplayDealVariant {
  tableIdx: number;
  label: string;
  /** Libellé court pour la liste (ex. « +120 »). */
  scoreText?: string;
  /** Nom du joueur attaquant sur cette table (en-tête colonne, fallback). */
  playerLabel?: string;
  /** Nom d'équipe pour l'en-tête de colonne (match par équipes). */
  teamLabel?: string;
  /** Équipe en attaque sur cette table — active le header dual entraînement. */
  attackTeam?: CompareReplayTeamSide;
  /** Équipe en défense sur cette table. */
  defenseTeam?: CompareReplayTeamSide;
  gameData: ReturnType<typeof gameHistoryToTrainingGameData>;
  replaySeatLabels: string[];
}

export interface CompareReplayDealEntry {
  dealIndex: number;
  label: string;
  gameData?: ReturnType<typeof gameHistoryToTrainingGameData>;
  replaySeatLabels?: string[];
  /** ID partie différé (navigation retour résultats) */
  partieId?: number;
  /** Deux tables duplicate (défi CIA) — préférer variants à gameData seul. */
  variants?: CompareReplayDealVariant[];
}

/** Extrait « Équipe A » / « Équipe B » depuis un titre « A vs B ». */
export function parseTeamMatchTitle(title: string): { equipe1Nom: string; equipe2Nom: string } | null {
  const m = title.trim().match(/^(.+?)\s+vs\s+(.+)$/i);
  if (!m) return null;
  const equipe1Nom = m[1].trim();
  const equipe2Nom = m[2].trim();
  if (!equipe1Nom || !equipe2Nom) return null;
  return { equipe1Nom, equipe2Nom };
}

/**
 * Complète teamLabel / attackTeam / defenseTeam sur les variantes d'un batch
 * (historique ancien, access-info indisponible, etc.).
 * Ordre par défaut : colonne 0 = équipe 1 en attaque, colonne 1 = équipe 2.
 */
export function enrichCompareReplayBatchWithTeams<
  T extends {
    title?: string;
    deals: Array<{ variants?: CompareReplayDealVariant[] }>;
  },
>(
  batch: T,
  teamChamp?: { equipe1Nom?: string; equipe2Nom?: string; equipe1Id?: number; equipe2Id?: number } | null,
): T {
  const fromTitle = batch.title ? parseTeamMatchTitle(batch.title) : null;
  const e1Nom = (teamChamp?.equipe1Nom || fromTitle?.equipe1Nom || '').trim();
  const e2Nom = (teamChamp?.equipe2Nom || fromTitle?.equipe2Nom || '').trim();
  if (!e1Nom || !e2Nom) return batch;

  const e1: CompareReplayTeamSide = {
    id: teamChamp?.equipe1Id != null ? Number(teamChamp.equipe1Id) : 1,
    nom: e1Nom,
  };
  const e2: CompareReplayTeamSide = {
    id: teamChamp?.equipe2Id != null ? Number(teamChamp.equipe2Id) : 2,
    nom: e2Nom,
  };

  let changed = false;
  const deals = batch.deals.map((deal) => {
    const variants = deal.variants;
    if (!variants || variants.length < 2) return deal;
    const nextVariants = variants.map((v, vi) => {
      if (v.teamLabel && v.attackTeam && v.defenseTeam) return v;
      changed = true;
      const attack = v.attackTeam ?? (vi === 0 ? e1 : e2);
      const defense = v.defenseTeam ?? (attack.id === e1.id ? e2 : e1);
      return {
        ...v,
        teamLabel: v.teamLabel || attack.nom,
        attackTeam: attack,
        defenseTeam: defense,
        label: v.scoreText ? `${attack.nom} (${v.scoreText})` : v.label,
      };
    });
    return nextVariants === variants ? deal : { ...deal, variants: nextVariants };
  });

  return changed ? { ...batch, deals: deals as T['deals'] } : batch;
}

export function resolveCompareReplayDealPayload(
  deal: CompareReplayDealEntry,
  variantIndex = 0,
): { gameData: unknown; replaySeatLabels: string[] } | null {
  if (deal.variants && deal.variants.length > 0) {
    const v = deal.variants[
      Math.min(Math.max(0, variantIndex), deal.variants.length - 1)
    ];
    return v ? { gameData: v.gameData, replaySeatLabels: v.replaySeatLabels } : null;
  }
  if (deal.gameData) {
    return {
      gameData: deal.gameData,
      replaySeatLabels: deal.replaySeatLabels ?? [],
    };
  }
  return null;
}

export function compareReplayBatchHasDealList(deals: CompareReplayDealEntry[]): boolean {
  return deals.length > 1 || deals.some((d) => (d.variants?.length ?? 0) > 1);
}

const REPLAY_POS_LABELS = ['sud', 'est', 'nord', 'ouest'] as const;

/**
 * Mains initiales duplicate (18 cartes / siège) à partir des cartes jouées.
 * Identiques pour les deux tables d'une même donne — indépendant du preneur.
 */
export function buildDuplicateInitialHandsFromTable(
  table: ReplayTable,
  playerCount = 4,
): Record<number, CardNumber[]> {
  const hands: Record<number, CardNumber[]> = {};
  for (let i = 0; i < playerCount; i++) {
    const label = REPLAY_POS_LABELS[i];
    hands[i] = sortHand([...(table.mains[label] || [])] as CardNumber[]);
  }
  return hands;
}

export function applyInitialHandsToTrainingGameData<T extends { initialHands?: Record<string | number, CardNumber[]> }>(
  gameData: T,
  initialHands: Record<number, CardNumber[]>,
): T {
  return {
    ...gameData,
    initialHands: Object.fromEntries(
      Object.entries(initialHands).map(([pos, hand]) => [pos, [...hand]]),
    ),
  };
}
